/**
 * Recovery Campaigns Card.
 *
 * Aggregates every re-engagement campaign that's been fired (from
 * `db.reengagement_sends`) and shows sent / failed / win-back per
 * campaign. Win-back = users whose `last_seen_at` is newer than their
 * `sent_at` for that campaign, i.e. the email got them back.
 *
 * Also lets the admin toggle the monthly auto-fire cron via the Settings
 * inline panel.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Megaphone, RefreshCw, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

export function RecoveryCampaignsCard() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [funnel, setFunnel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [a, b, f] = await Promise.all([
      apiFetch('/api/admin/reengagement/campaigns'),
      apiFetch('/api/admin/reengagement/recurring'),
      apiFetch('/api/admin/reengagement/funnel'),
    ]);
    setLoading(false);
    if (!a.error) setCampaigns(a.data?.campaigns || []);
    if (!b.error) setCfg(b.data);
    if (!f.error) setFunnel(f.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCfg = async () => {
    if (!cfg) return;
    setSavingCfg(true);
    const { error } = await apiFetch('/api/admin/reengagement/recurring', {
      method: 'PUT',
      body: {
        enabled: cfg.enabled,
        dormant_days: Number(cfg.dormant_days),
        cap: Number(cfg.cap),
      },
    });
    setSavingCfg(false);
    if (error) { toast.error(error || 'Failed to save'); return; }
    toast.success('Recurring config saved');
    load();
  };

  return (
    <Card data-testid="recovery-campaigns-card">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-[#00E5CC]" />
              Recovery Campaigns
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              History of every &quot;we miss you&quot; email blast. Win-back = users who came back to the app after being emailed.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="recovery-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Recurring config row */}
        {cfg && (
          <div className="rounded-md border bg-muted/30 p-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <CalendarClock className="h-5 w-5 text-[#00E5CC]" />
                <div>
                  <p className="text-sm font-medium">Monthly auto re-engagement</p>
                  <p className="text-xs text-muted-foreground">{cfg.schedule}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label htmlFor="recovery-enabled" className="text-xs">Enabled</Label>
                  <Switch
                    id="recovery-enabled"
                    checked={!!cfg.enabled}
                    onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                    data-testid="recovery-enabled-switch"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Label htmlFor="recovery-dormant-days" className="text-xs">Dormant ≥</Label>
                  <Input
                    id="recovery-dormant-days"
                    type="number" min={1} max={365}
                    value={cfg.dormant_days}
                    onChange={(e) => setCfg({ ...cfg, dormant_days: e.target.value })}
                    className="w-20 h-8"
                    data-testid="recovery-dormant-days-input"
                  />
                  <span className="text-xs text-muted-foreground">days</span>
                </div>
                <div className="flex items-center gap-1">
                  <Label htmlFor="recovery-cap" className="text-xs">Cap</Label>
                  <Input
                    id="recovery-cap"
                    type="number" min={1} max={2000}
                    value={cfg.cap}
                    onChange={(e) => setCfg({ ...cfg, cap: e.target.value })}
                    className="w-24 h-8"
                    data-testid="recovery-cap-input"
                  />
                </div>
                <Button size="sm" onClick={saveCfg} disabled={savingCfg} data-testid="recovery-save-cfg-btn">
                  {savingCfg ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
            {cfg.updated_at && (
              <p className="text-[11px] text-muted-foreground mt-2">
                Last updated {new Date(cfg.updated_at).toLocaleString()} by {cfg.updated_by || 'system'}
              </p>
            )}
          </div>
        )}

        {/* Funnel: dormant → emailed → clicked → returned → re-engaged */}
        {funnel?.stages?.length > 0 && (
          <div className="rounded-md border bg-gradient-to-br from-[#00E5CC]/5 to-transparent p-3">
            <p className="text-sm font-medium mb-3">Conversion funnel (all campaigns)</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {funnel.stages.map((s) => (
                <div
                  key={s.key}
                  className="rounded border bg-background p-2 text-center"
                  data-testid={`funnel-stage-${s.key}`}
                >
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground truncate" title={s.label}>
                    {s.label}
                  </p>
                  <p className="text-lg font-bold tabular-nums">{s.count.toLocaleString()}</p>
                  {s.rate_of_prev !== null && (
                    <p className="text-[10px] text-[#00b3a0] dark:text-[#00E5CC] tabular-nums">
                      {s.rate_of_prev}% of prev
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Campaign history */}
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Failed</TableHead>
                <TableHead>Recipients</TableHead>
                <TableHead>Opens</TableHead>
                <TableHead>Open %</TableHead>
                <TableHead>Clicks</TableHead>
                <TableHead>CTR</TableHead>
                <TableHead>Win-back</TableHead>
                <TableHead>Win-back %</TableHead>
                <TableHead>Unsubs</TableHead>
                <TableHead>Unsub %</TableHead>
                <TableHead>Last send</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-6">
                    <RefreshCw className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-6 text-sm text-muted-foreground">
                    No re-engagement campaigns have been fired yet. Use the &quot;Bulk Reach-Out&quot; button on the Users tab to send your first one.
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.flatMap((c) => {
                  const rows = [(
                    <TableRow key={c.campaign} data-testid={`campaign-row-${c.campaign}`}>
                      <TableCell className="font-mono text-xs">{c.campaign}</TableCell>
                      <TableCell className="tabular-nums">{c.total_sent}</TableCell>
                      <TableCell className="tabular-nums">
                        {c.total_failed > 0 ? (
                          <Badge variant="destructive" className="text-xs">{c.total_failed}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">{c.unique_recipients}</TableCell>
                      <TableCell className="tabular-nums">{c.open_count ?? 0}</TableCell>
                      <TableCell className="tabular-nums">
                        <Badge variant="outline" className="border-amber-500/40 text-amber-600 dark:text-amber-400">
                          {c.open_rate ?? 0}%
                        </Badge>
                      </TableCell>
                      <TableCell className="tabular-nums">{c.click_count ?? 0}</TableCell>
                      <TableCell className="tabular-nums">
                        <Badge variant="outline" className="border-blue-500/40 text-blue-600 dark:text-blue-400">
                          {c.click_through_rate ?? 0}%
                        </Badge>
                      </TableCell>
                      <TableCell className="tabular-nums">{c.winback_count}</TableCell>
                      <TableCell className="tabular-nums">
                        <Badge
                          variant="outline"
                          className={c.winback_rate >= 20
                            ? 'border-green-500/40 text-green-600 dark:text-green-400'
                            : 'border-muted-foreground/30 text-muted-foreground'}
                        >
                          {c.winback_rate}%
                        </Badge>
                      </TableCell>
                      <TableCell className="tabular-nums">{c.unsub_count ?? 0}</TableCell>
                      <TableCell className="tabular-nums">
                        <Badge
                          variant="outline"
                          className={c.unsub_rate > 2
                            ? 'border-red-500/40 text-red-600 dark:text-red-400'
                            : 'border-muted-foreground/30 text-muted-foreground'}
                          title="Unsub rates above 2% suggest the audience or copy is mismatched."
                        >
                          {c.unsub_rate ?? 0}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {c.last_sent_at ? new Date(c.last_sent_at).toLocaleDateString() : '—'}
                      </TableCell>
                    </TableRow>
                  )];
                  // Per-variant A/B breakdown rows (only when 2+ variants present).
                  if ((c.variants || []).length > 1) {
                    c.variants.forEach((v) => rows.push(
                      <TableRow
                        key={`${c.campaign}-${v.variant}`}
                        className="bg-muted/40"
                        data-testid={`campaign-variant-${c.campaign}-${v.variant}`}
                      >
                        <TableCell className="pl-8 text-xs italic text-muted-foreground">
                          ↳ subject: {v.variant}
                        </TableCell>
                        <TableCell className="tabular-nums text-xs">{v.sent}</TableCell>
                        <TableCell />
                        <TableCell className="tabular-nums text-xs">{v.recipients}</TableCell>
                        <TableCell className="tabular-nums text-xs">{v.opens}</TableCell>
                        <TableCell className="tabular-nums text-xs">{v.open_rate}%</TableCell>
                        <TableCell className="tabular-nums text-xs">{v.clicks}</TableCell>
                        <TableCell className="tabular-nums text-xs">{v.click_through_rate}%</TableCell>
                        <TableCell className="tabular-nums text-xs">{v.winbacks}</TableCell>
                        <TableCell className="tabular-nums text-xs">{v.winback_rate}%</TableCell>
                        <TableCell className="tabular-nums text-xs">{v.unsubs}</TableCell>
                        <TableCell className="tabular-nums text-xs">{v.unsub_rate}%</TableCell>
                        <TableCell />
                      </TableRow>
                    ));
                  }
                  return rows;
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

export default RecoveryCampaignsCard;
